import type { AddressSearchResultWithSubBuildings } from './addressQueryResponseBuilder';
import type { ComparableAddress } from './validateAddressMatch';

export type AddressDetailFields = {
  streetNumber: string;
  street: string;
  poBox: string;
  externalAVSAddressId: string;
  address: string;
};

export type ResolvedAddressFields = ComparableAddress & AddressDetailFields;

const EMPTY_ADDRESS_DETAILS: AddressDetailFields = {
  streetNumber: '',
  street: '',
  poBox: '',
  externalAVSAddressId: '',
  address: '',
};

/**
 * Builds the full set of address fields to submit for either the mailing or
 * merchandise-location address.
 *
 * `addressLine1`, `subBuilding`, `city`, `regionOrStateCode` and
 * `postalCode` always come straight from the form, since they're editable
 * inputs.
 *
 * `streetNumber`, `street`, `poBox`, `externalAVSAddressId` and `address`
 * (the vendor's full formatted address string) are never presented as
 * editable form inputs - they only exist as vendor (Melissa/AVS)-parsed
 * components of whichever address the user selected from search. They must
 * not be stored in form state (there's no input to keep them in sync, so
 * they'd go stale the moment the user hand-edits the address afterwards) -
 * instead they're read directly from the tracked `selectedAddress` here,
 * and only when the address is still valid (i.e. still matches what was
 * selected). Once invalid there's nothing to fall back to, so they're left
 * empty.
 */
export function buildAddressSubmissionFields(
  address: ComparableAddress,
  isValid: boolean,
  selectedAddress: AddressSearchResultWithSubBuildings | null | undefined,
): ResolvedAddressFields {
  const details: AddressDetailFields =
    isValid && selectedAddress
      ? {
          streetNumber: selectedAddress.streetNumber ?? '',
          street: selectedAddress.street ?? '',
          poBox: selectedAddress.poBox ?? '',
          externalAVSAddressId: selectedAddress.id ?? '',
          address: selectedAddress.address ?? '',
        }
      : EMPTY_ADDRESS_DETAILS;

  return {
    ...address,
    ...details,
  };
}
